import React, { useState } from 'react';
import { Box, Typography, Container, Grid, Link as MuiLink, useTheme, Button, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { Settings, Cookie } from '@mui/icons-material';
import CookieBanner from './CookieBanner';
import GDPRConsentModal from './GDPRConsentModal';
import { useConsent } from '../hooks/useConsent';

const Footer: React.FC = () => {
  const theme = useTheme();
  const { clearConsent, hasValidConsent } = useConsent();
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const handleReopenCookieSettings = () => {
    if (hasValidConsent) {
      // Wenn bereits Einwilligung vorhanden, zeige Detail-Modal
      setShowDetailModal(true);
    } else {
      // Sonst Banner neu anzeigen
      clearConsent();
      setShowCookieSettings(true);
    }
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
  };

  return (
    <Box
      component="footer"
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper}90 40%, ${theme.palette.primary.main}20 80%, ${theme.palette.primary.main}15 100%)`,
        color: 'white',
        py: 4,
        mt: 'auto',
        borderTop: `1px solid ${theme.palette.primary.main}30`
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom color="primary">
              Smart Storage Checker
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Bewertung von Hybrid-Speichersystemen.<br />
              Entwickelt von Team Dominic, Raul, Mateo mit InnCharge.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Alle Rechte vorbehalten.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Legal
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MuiLink
                    component={Link}
                    to="/impressum"
                    color="text.secondary"
                    underline="hover"
                    sx={{
                      transition: 'color 0.3s ease',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    Impressum
                  </MuiLink>
                  <MuiLink
                    component={Link}
                    to="/datenschutz"
                    color="text.secondary"
                    underline="hover"
                    sx={{
                      transition: 'color 0.3s ease',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    Datenschutz
                  </MuiLink>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Navigation
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MuiLink
                    component={Link}
                    to="/"
                    color="text.secondary"
                    underline="hover"
                    sx={{
                      transition: 'color 0.3s ease',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    Home
                  </MuiLink>
                  <MuiLink
                    component={Link}
                    to="/upload"
                    color="text.secondary"
                    underline="hover"
                    sx={{
                      transition: 'color 0.3s ease',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    CSV Upload
                  </MuiLink>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.primary.main}20` }}>
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="caption" color="text.secondary">
              Innovative Energiespeicher-Technologie für eine nachhaltige Zukunft 🌱⚡
            </Typography>
            
            {/* Cookie-Einstellungen Reopen-Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Cookie />}
              onClick={handleReopenCookieSettings}
              sx={{
                borderColor: theme.palette.primary.main + '40',
                color: 'text.secondary',
                fontSize: '0.75rem',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  color: 'primary.main',
                  backgroundColor: theme.palette.primary.main + '10'
                }
              }}
            >
              Cookie-Einstellungen
            </Button>
          </Box>
        </Box>
      </Container>
      
      {/* Cookie-Banner für Reopen-Funktion */}
      {showCookieSettings && (
        <CookieBanner onOpenDetailModal={() => setShowDetailModal(true)} />
      )}
      
      {/* Detaillierte GDPR-Modal */}
      <GDPRConsentModal
        open={showDetailModal}
        onClose={handleCloseDetailModal}
        onAccept={handleCloseDetailModal}
        onDecline={handleCloseDetailModal}
      />
    </Box>
  );
};

export default Footer;

